import { BrickwallError, DEFAULT_PERPEND_GAP_MM, WallModel } from '@bw/core';
import type { BrickId } from '@bw/core';
import { ProjectDocument } from './project.js';
import type { Wall } from './project.js';
import {
  DEFAULT_BRICK_COLOUR,
  makeMaterial,
  nextMaterialId,
  paletteMaterials,
} from './materials.js';
import type { Material, WallPlacement } from './types.js';
import { DEFAULT_PLACEMENT } from './types.js';

export const LEGACY_FORMAT = 'brickwall-designer';
export const LEGACY_VERSIONS = [1, 2] as const;
export const LEGACY_EXTENSION = '.brickwall.json';

export interface BrickProblem {
  readonly index: number;
  readonly id: BrickId | null;
  readonly x: number;
  readonly y: number;
  readonly kind: string;
  readonly reason: string;
}

export class LegacyImportError extends Error {
  override readonly name = 'LegacyImportError';
  readonly problems: readonly BrickProblem[];
  /** True when opening the file with the offending bricks omitted would work. */
  readonly repairable: boolean;

  constructor(message: string, problems: readonly BrickProblem[] = [], repairable = false) {
    super(message);
    this.problems = problems;
    this.repairable = repairable;
  }
}

export interface ImportResult {
  readonly project: ProjectDocument;
  readonly dropped: readonly BrickProblem[];
}

export interface ImportOptions {
  /** Name for the project and its single wall. */
  readonly name?: string;
  /** Omit bricks that fail clearance instead of refusing the whole file. */
  readonly repair?: boolean;
}

export interface WallImportOptions {
  /** Name for the imported wall. */
  readonly name?: string;
  /** Omit bricks that fail clearance instead of refusing the whole file. */
  readonly repair?: boolean;
  /** Where the wall goes in plan. Defaults to the origin, like a new wall. */
  readonly placement?: WallPlacement;
}

export interface WallImportResult {
  readonly wall: Wall;
  readonly dropped: readonly BrickProblem[];
  /** Colours the file used that the project did not already have. */
  readonly addedMaterials: readonly Material[];
}

/** One brick as the legacy file records it, before it is given a material. */
interface LegacyBrick {
  readonly index: number;
  readonly id: unknown;
  readonly kind: unknown;
  readonly x: unknown;
  readonly y: unknown;
  readonly rotation: unknown;
  /** Already normalised to upper-case six-digit hex. */
  readonly colour: string;
}

interface LegacyFile {
  readonly perpendGapMm: number;
  readonly bricks: readonly LegacyBrick[];
  /** The distinct colours, in the order the file first uses them. */
  readonly colours: readonly string[];
}

function fail(message: string): never {
  throw new LegacyImportError(message);
}

/**
 * Read a `brickwall-designer` v1 or v2 file (design document D3).
 *
 * Stops at the brick records: nothing here places a brick or invents a
 * material, because who owns the materials differs between importing a file as
 * a project of its own and importing it as one wall of a project that already
 * has a palette.
 */
function parseLegacy(text: string): LegacyFile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    fail('This file is not valid JSON');
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    fail('This is not a Brickwall Designer project');
  }
  const data = raw as Record<string, unknown>;
  if (data['format'] !== LEGACY_FORMAT) fail('This is not a Brickwall Designer project');
  const version = data['version'];
  if (version !== 1 && version !== 2) {
    fail(`Unsupported project version: ${String(version)}`);
  }
  const bricks = data['bricks'];
  if (!Array.isArray(bricks)) fail('Project does not contain a valid brick list');

  // Clause 14.1: `mortar_gap_mm` holds the BED joint, not the perpend, which
  // is why this fallback order matters and must not be simplified.
  const perpendGapMm = firstNumber(
    data['perpend_joint_mm'],
    data['perpend_gap_mm'],
    data['mortar_gap_mm'],
    DEFAULT_PERPEND_GAP_MM,
  );

  const colours: string[] = [];
  const records = bricks.map((entry, index): LegacyBrick => {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      fail('Each brick record must be an object');
    }
    const brick = entry as Record<string, unknown>;
    const rawColour = typeof brick['colour'] === 'string' ? brick['colour'] : DEFAULT_BRICK_COLOUR;
    const colour = /^#[0-9a-fA-F]{6}$/.test(rawColour)
      ? rawColour.toUpperCase()
      : DEFAULT_BRICK_COLOUR;
    if (!colours.includes(colour)) colours.push(colour);
    return {
      index,
      id: brick['id'],
      kind: brick['kind'],
      x: brick['x'],
      y: brick['y'],
      rotation: brick['rotation'],
      colour,
    };
  });

  return { perpendGapMm, bricks: records, colours };
}

/**
 * Lay the file's bricks into a model, one at a time.
 *
 * One at a time so a failure can be located rather than just reported: unlike
 * the desktop tool, which refuses a whole file for one bad brick, this can say
 * exactly which bricks are at fault and open the rest.
 */
function layBricks(
  model: WallModel,
  bricks: readonly LegacyBrick[],
  materialFor: (colour: string) => string,
  repair: boolean,
): BrickProblem[] {
  const problems: BrickProblem[] = [];
  for (const record of bricks) {
    try {
      model.addBrick(String(record.kind), record.x as number, record.y as number, {
        rotation: record.rotation as number,
        materialId: materialFor(record.colour),
        brickId: record.id as BrickId,
        validate: true,
      });
    } catch (error) {
      problems.push({
        index: record.index,
        id: typeof record.id === 'number' ? record.id : null,
        x: typeof record.x === 'number' ? record.x : Number.NaN,
        y: typeof record.y === 'number' ? record.y : Number.NaN,
        kind: String(record.kind),
        reason: error instanceof Error ? error.message : String(error),
      });
      if (!repair) {
        const clearanceOnly = error instanceof BrickwallError && /clearance/.test(error.message);
        throw new LegacyImportError(
          clearanceOnly
            ? `${problems.length} brick could not be placed with valid clearance`
            : (error as Error).message,
          problems,
          clearanceOnly,
        );
      }
    }
  }
  return problems;
}

/**
 * One-way import of `brickwall-designer` v1 and v2 as a project of its own
 * (design document D3).
 */
export function importLegacyProject(text: string, options: ImportOptions = {}): ImportResult {
  const file = parseLegacy(text);

  // The file owns the palette here, so its colours are numbered from m1 in the
  // order it uses them.
  const materials = file.colours.map((colour, index) => makeMaterial(`m${index + 1}`, colour));
  if (materials.length === 0) materials.push(makeMaterial('m1', DEFAULT_BRICK_COLOUR));
  const byColour = new Map(file.colours.map((colour, index) => [colour, `m${index + 1}`]));

  const model = new WallModel([], file.perpendGapMm);
  const problems = layBricks(
    model,
    file.bricks,
    (colour) => byColour.get(colour) ?? 'm1',
    options.repair === true,
  );

  const name = options.name ?? 'Imported wall';
  const project = new ProjectDocument(name);
  // Keep the file's own colours first, then top up with the rest of the
  // palette so the paint tool has a full set to work with.
  const used = new Set(materials.map((material) => material.colour));
  const extras = paletteMaterials()
    .filter((material) => !used.has(material.colour))
    .map((material, index) => ({ ...material, id: `m${materials.length + index + 1}` }));
  project.setMaterials([...materials, ...extras]);
  const seeded = project.walls[0];
  const wall: Wall = {
    id: 'w1',
    name,
    placement: DEFAULT_PLACEMENT,
    baseMaterialId: (materials[0] as Material).id,
    envelope: null,
    span: null,
    openings: [],
    model,
    extra: {},
    brickExtra: new Map(),
  };
  project.adoptWall(wall);
  if (seeded !== undefined) project.removeWall(seeded.id);
  project.setActiveWall(wall.id);
  return { project, dropped: problems };
}

/**
 * Import the same file as ONE MORE WALL of a project that is already open.
 *
 * The desktop tool wrote a file per elevation, so a house arrives as four
 * files. Importing each as a project of its own and then having no way to put
 * them together is the wrong shape for that: this adds the wall alongside the
 * ones already drawn, and the plan stage arranges them.
 *
 * Two things differ from importing a project. The palette belongs to the
 * project, not to the file, so colours resolve against it BY COLOUR — four
 * elevations of one house share one brick red rather than contributing four of
 * them. And the project must not be half-changed by a file that will not open:
 * the wall is built standing on its own and only handed over once every brick
 * is laid, so a refusal leaves the document exactly as it was.
 */
export function importLegacyWall(
  project: ProjectDocument,
  text: string,
  options: WallImportOptions = {},
): WallImportResult {
  const file = parseLegacy(text);

  // Resolved against a COPY of the project's materials, so the ids are the
  // ones the bricks will really reference while nothing is committed yet.
  const materials = [...project.materials];
  const added: Material[] = [];
  const byColour = new Map<string, string>();
  for (const colour of file.colours) {
    const existing = materials.find((material) => material.colour === colour);
    if (existing !== undefined) {
      byColour.set(colour, existing.id);
      continue;
    }
    const material = makeMaterial(nextMaterialId(materials), colour);
    materials.push(material);
    added.push(material);
    byColour.set(colour, material.id);
  }
  const fallback = project.materials[0]?.id ?? 'm1';

  const model = new WallModel([], file.perpendGapMm);
  const dropped = layBricks(
    model,
    file.bricks,
    (colour) => byColour.get(colour) ?? fallback,
    options.repair === true,
  );

  // Past here nothing can throw, so the project changes all at once or not at
  // all.
  if (added.length > 0) project.setMaterials(materials);
  const wall: Wall = {
    id: project.nextWallId,
    name: options.name ?? 'Imported wall',
    placement: options.placement ?? DEFAULT_PLACEMENT,
    baseMaterialId: byColour.get(file.colours[0] ?? '') ?? fallback,
    envelope: null,
    span: null,
    openings: [],
    model,
    extra: {},
    brickExtra: new Map(),
  };
  project.adoptWall(wall);
  project.setActiveWall(wall.id);
  return { wall, dropped, addedMaterials: added };
}

/** Sniff a file's format without fully parsing it. */
export function detectFormat(text: string): 'brickwall-project' | 'brickwall-designer' | 'unknown' {
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    if (data['format'] === 'brickwall-project') return 'brickwall-project';
    if (data['format'] === LEGACY_FORMAT) return LEGACY_FORMAT;
  } catch {
    // fall through
  }
  return 'unknown';
}

function firstNumber(...values: unknown[]): number {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return DEFAULT_PERPEND_GAP_MM;
}
